import React, {useState, useEffect} from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    List,
    ListItemButton,
    ListItemText,
    Typography
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import User from "../backend/User";
import AccountData from "../backend/dataservices/AccountData";

/**
 * Kontoauswahl im Kontoverbund.
 *
 * Benutzer mit konfigurierten User-Relationen koennen nicht unter ihrem eigenen
 * Konto arbeiten — sie waehlen hier eines ihrer Ziel-Konten und uebernehmen es.
 * Die Seite ist zugleich der Rueckweg beim Kontowechsel.
 */
function SelectAccountPage() {
    const [accounts, setAccounts] = useState([]);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [switching, setSwitching] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        AccountData.getMyAccounts().then(data => {
            const list = Array.isArray(data?.accounts) ? data.accounts : [];
            // Keine Konten heisst: der Server sperrt diesen Benutzer nicht (mehr).
            // Das Token kann noch mustSelectAccount tragen, wenn ein Admin die
            // Relationen nach dem Login entfernt hat — dann darf die Seite keine
            // Sackgasse sein, sondern muss weiterleiten.
            if (list.length === 0) {
                window.location = process.env.PUBLIC_URL + (User.getHomepage() || '/start');
                return;
            }
            setAccounts(list);
            setCurrentUserId(data?.currentUserId ?? null);
            setLoading(false);
        }).catch(() => {
            setError('Die Konten konnten nicht geladen werden.');
            setLoading(false);
        });
    }, []);

    const handleSelect = (account) => {
        if (account.U_TenantLocked) return;
        setSwitching(account.U_ID);
        setError(null);
        AccountData.switchAccount(account.U_ID).then(response => {
            User.switchAccount(response.token);
            // Harter Reload: setzt die statischen Felder in User zurueck und
            // verhindert, dass Komponenten Daten des vorherigen Kontos zeigen.
            window.location = process.env.PUBLIC_URL + (User.getHomepage() || '/start');
        }).catch(err => {
            setError(err?.response?.data?.error || 'Der Kontowechsel ist fehlgeschlagen.');
            setSwitching(null);
        });
    };

    const handleLogout = () => {
        User.logout();
        window.location = process.env.PUBLIC_URL + "/login";
    };

    return (
        <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: 'translate(-50%, -50%)'
        }}>
            <Card sx={{maxWidth: 520, minWidth: 380}}>
                <CardHeader
                    title={"WebTrain"}
                    subheader={"Konto auswählen"}
                    style={{backgroundColor: "rgb(0,150,136)", color: 'white'}}
                    subheaderTypographyProps={{color: 'rgba(255,255,255,0.8)'}}
                />
                <CardContent sx={{py: 3}}>
                    <Typography variant="body1" color="text.secondary" sx={{mb: 2}}>
                        Wählen Sie das Konto, mit dem Sie arbeiten möchten. Sie können
                        später jederzeit über die Kopfzeile wechseln.
                    </Typography>

                    {loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                            <CircularProgress/>
                        </Box>
                    ) : (
                        <>
                            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

                            {accounts.length > 0 && (
                                <List sx={{border: '1px solid #e0e0e0', borderRadius: 1, p: 0, mb: 2}}>
                                    {accounts.map((account, index) => (
                                        <React.Fragment key={account.U_ID}>
                                            {index > 0 && <Divider component="li"/>}
                                            <ListItemButton
                                                onClick={() => handleSelect(account)}
                                                disabled={switching !== null || !!account.U_TenantLocked}
                                                selected={account.U_ID === currentUserId}
                                            >
                                                <ListItemText
                                                    primary={account.U_FullName || account.U_Benutzername}
                                                    secondary={
                                                        [
                                                            account.U_TenantName
                                                                ? `${account.U_TenantName} (${account.U_TenantCode})`
                                                                : 'Kein Tenant',
                                                            account.U_Rolle || 'Keine Rolle'
                                                        ].join(' · ')
                                                    }
                                                />
                                                {account.U_TenantLocked ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<LockIcon/>}
                                                        label="Gesperrt"
                                                        color="default"
                                                    />
                                                ) : account.U_ID === currentUserId ? (
                                                    <Chip size="small" label="Aktiv" color="primary"/>
                                                ) : null}
                                                {switching === account.U_ID && (
                                                    <CircularProgress size={20} sx={{ml: 1}}/>
                                                )}
                                            </ListItemButton>
                                        </React.Fragment>
                                    ))}
                                </List>
                            )}

                            <Button
                                variant="text"
                                fullWidth
                                onClick={handleLogout}
                                sx={{color: 'text.secondary'}}
                            >
                                Abmelden
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

export default SelectAccountPage;
